<template>
  <div class="w-full lg:flex lg:gap-2 lg:items-stretch">
    <div
        ref="scrollContainer"
        class="w-full overflow-x-auto overflow-y-hidden sticky snap-x snap-mandatory lg:snap-none no-scrollbar scroll-smooth lg:w-auto lg:min-w-0 lg:flex-1"
        @scroll.passive="watchScroll"
        :class="{
          'fixed insert-0 z-50 h-screen w-screen my-auto content-center': popupImage
        }"
    >
      <div v-if="popupImage" class="fixed bottom-6 right-6 z-50 w-10 h-10" @click="$emit('popupImage', false)"
           :aria-label="t(pageStr + '.close')" role="button">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="size-6">
          <path fill-rule="evenodd"
                d="M3.22 3.22a.75.75 0 0 1 1.06 0l3.97 3.97V4.5a.75.75 0 0 1 1.5 0V9a.75.75 0 0 1-.75.75H4.5a.75.75 0 0 1 0-1.5h2.69L3.22 4.28a.75.75 0 0 1 0-1.06Zm17.56 0a.75.75 0 0 1 0 1.06l-3.97 3.97h2.69a.75.75 0 0 1 0 1.5H15a.75.75 0 0 1-.75-.75V4.5a.75.75 0 0 1 1.5 0v2.69l3.97-3.97a.75.75 0 0 1 1.06 0ZM3.75 15a.75.75 0 0 1 .75-.75H9a.75.75 0 0 1 .75.75v4.5a.75.75 0 0 1-1.5 0v-2.69l-3.97 3.97a.75.75 0 0 1-1.06-1.06l3.97-3.97H4.5a.75.75 0 0 1-.75-.75Zm10.5 0a.75.75 0 0 1 .75-.75h4.5a.75.75 0 0 1 0 1.5h-2.69l3.97 3.97a.75.75 0 0 1-1.06 1.06l-3.97-3.97v2.69a.75.75 0 0 1-1.5 0V15Z"
                clip-rule="evenodd"/>
        </svg>

      </div>
      <ul
          class="grid grid-flow-col auto-cols-max lg:grid-flow-row lg:auto-cols-auto lg:gap-2 p-0.5 bg-white relative"
          :class="layoutVariant === 5 ? 'lg:grid-cols-1' : 'lg:grid-cols-2'"
      >
        <!-- row 1 col 1: main item image -->
        <li id="imageIdx-1" class="w-screen lg:w-full bg-white snap-center lg:snap-align-none"
            :class="{ 'lg:max-w-[1500px] lg:mx-auto': layoutVariant === 5 }"
            style="aspect-ratio: 1/1">
          <item-image
              :image-url="image.filename"
              :image-layer-url="image.filename_layer"
              :main-metal="image.main_metal"
              :is-wedding-ring=true
              :zoom=true
          />
        </li>

        <!-- row 1 col 2: image.positions[0] (variant 1/3/6) or image.additionalItem (variant 2/4) -->
        <li
            v-if="layoutVariant !== 5"
            id="imageIdx0"
            class="w-screen lg:w-full bg-white snap-center lg:snap-align-none"
            style="aspect-ratio: 1/1"
        >
          <item-image
              v-if="layoutVariant === 1 || layoutVariant === 3 || layoutVariant === 6"
              :key="image.positions[0].filename"
              :image-url="image.positions[0].filename"
              :image-layer-url="image.positions[0].filename_layer"
              :main-metal="image.main_metal"
              :zoom=true
          />
          <item-image
              v-else
              :image-url="image.additionalItem.filename"
              :image-layer-url="image.additionalItem.filename_layer"
              :main-metal="image.additionalItem.main_metal"
              :is-wedding-ring=true
              :zoom=true
          />
        </li>

        <!-- row 2: emotional set image, split in a left and a right part -->
        <template v-if="layoutVariant !== 5">
          <li id="imageIdxEmotionalLeft" class="w-screen lg:w-full bg-white snap-center lg:snap-align-none"
              style="aspect-ratio: 1/1">
            <item-v3-image-animation-wedding-rings
                v-if="emotionalImageAnimated"
                :image-url="emotionalImageUrl"
                image-position="left"
            />
            <img
                v-else
                :src="emotionalImageUrl"
                class="w-full h-full object-cover object-left"
                alt=""
            />
          </li>
          <li id="imageIdxEmotionalRight" class="w-screen lg:w-full bg-white snap-center lg:snap-align-none"
              style="aspect-ratio: 1/1">
            <item-v3-image-animation-wedding-rings
                v-if="emotionalImageAnimated"
                :image-url="emotionalImageUrl"
                image-position="right"
            />
            <img
                v-else
                :src="emotionalImageUrl"
                class="w-full h-full object-cover object-right"
                alt=""
            />
          </li>
        </template>

        <!-- row 3 col 1: image.additionalItem.positions[0] (variant 1/2) or image.additionalItem (variant 3) -->
        <li
            v-if="layoutVariant === 1 || layoutVariant === 2 || layoutVariant === 3"
            id="imageIdx1"
            class="w-screen lg:w-full bg-white snap-center lg:snap-align-none"
            style="aspect-ratio: 1/1"
        >
          <item-image
              v-if="layoutVariant === 1 || layoutVariant === 2"
              :key="image.additionalItem.positions[0].filename"
              :image-url="image.additionalItem.positions[0].filename"
              :image-layer-url="image.additionalItem.positions[0].filename_layer"
              :main-metal="image.main_metal"
              :zoom=true
          />
          <item-image
              v-else
              :image-url="image.additionalItem.filename"
              :image-layer-url="image.additionalItem.filename_layer"
              :main-metal="image.additionalItem.main_metal"
              :is-wedding-ring=true
              :zoom=true
          />
        </li>

        <!-- row 3 col 2: image.additionalItem (variant 1) or brand logo placeholder (variant 2/3) -->
        <li
            v-if="layoutVariant === 1 || layoutVariant === 2 || layoutVariant === 3"
            id="imageIdx2"
            class="w-screen lg:w-full bg-white snap-center lg:snap-align-none"
            style="aspect-ratio: 1/1"
        >
          <item-image
              v-if="layoutVariant === 1"
              :image-url="image.additionalItem.filename"
              :image-layer-url="image.additionalItem.filename_layer"
              :main-metal="image.additionalItem.main_metal"
              :is-wedding-ring=true
              :zoom=true
          />
          <item-v2-item-image-brand-logo
              v-else
              :main-metal="image.main_metal"
              :brand="brandLogoId"
          />
        </li>
      </ul>
    </div>
  </div>
</template>
<script setup lang="ts">
/**
 Variant 1
 image.filename exists
 image.positions[0].filename exists
 image.additionalItem.filename exists
 image.additionalItem.positions[0].filename exists

 Grid:
 row 1
 col 1: image.filename
 col 2: image.positions[0].filename
 row 2
 col 1: emotional set image left part
 col 2: emotional set image right part
 row 3
 col 1: image.additionalItem.positions[0].filename
 col 2: image.additionalItem.filename

 Variant 2
 image.filename exists
 image.positions[0].filename not exists
 image.additionalItem.filename exists
 image.additionalItem.positions[0].filename exists

 Grid:
 row 1
 col 1: image.filename
 col 2:  image.additionalItem.filename
 row 2
 col 1: emotional set image left part
 col 2: emotional set image right part
 row 3
 col 1: image.additionalItem.positions[0].filename
 col 2: placeholder - logo of the brand


 Variant 3
 image.filename exists
 image.positions[0].filename exists
 image.additionalItem.filename exists
 image.additionalItem.positions[0].filename not exists

 Grid:
 row 1
 col 1: image.filename
 col 2: image.positions[0].filename
 row 2
 col 1: emotional set image left part
 col 2: emotional set image right part
 row 3
 col 1: image.additionalItem.filename
 col 2: placeholder - logo of the brand

 Variant 4
 image.filename exists
 image.positions[0].filename not exists
 image.additionalItem.filename exists
 image.additionalItem.positions[0].filename not exists

 Grid:
 row 1
 col 1: image.filename
 col 2:  image.additionalItem.filename
 row 2
 col 1: emotional set image left part
 col 2: emotional set image right part
 row 3 is empty / does not exist

 Variant 5
 image.filename exists
 image.positions[0].filename not exists
 image.additionalItem.filename not exists
 image.additionalItem.positions[0].filename not exists

 Grid:
 Changes to one row and one col
 image.filename is placed it the middle of the only col/node but with a max width of 1500px

 Variant 6
 image.filename exists
 image.positions[0].filename exists
 image.additionalItem.filename not exists
 image.additionalItem.positions[0].filename not exists

 Grid:
 row 1
 col 1: image.filename
 col 2: image.positions[0].filename
 row 2
 col 1: emotional set image left part
 col 2: emotional set image right part
 row 3 is empty / does not exist

 */
import ItemImage from "@/components/itemImage.vue";
import ItemV2ItemImageBrandLogo from "@/components/itemV2/itemV2ItemImageBrandLogo.vue";
import ItemV3ImageAnimationWeddingRings from "@/components/itemV3/itemV3ImageAnimationWeddingRings.vue";
import {computed, ref, watch} from "vue";
import {useI18n} from "vue-i18n";

const {t} = useI18n();
const scrollContainer = ref<HTMLElement | null>(null);
const props = defineProps([
  "image",
  "imageIdx",
  "popupImage",
  "itemNumber",
  "emotionalImageAnimated"
]);
const pageStr = "components.itemV2.itemV2ImageGrid"

/**
 image idx
 */
const emits = defineEmits([
  "switchImage",
  "popupImage"
]);

let isProgrammaticScroll = false;
let timeout: any = null;

const watchScroll = () => {
  if (isProgrammaticScroll || !scrollContainer.value) return;
  getImageInViewport();
}

watch(
    () => props.imageIdx,
    (imageIdx: string | number) => {
      isProgrammaticScroll = true;
      if (timeout) clearTimeout(timeout);

      scrollContainer.value?.querySelector("#imageIdx" + imageIdx)?.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest"
      });

      // Reset the flag after the smooth scroll is likely finished
      timeout = setTimeout(() => {
        isProgrammaticScroll = false;
      }, 500);
    },
    {deep: true},
);

const getImageInViewport = () => {
  checkImageInViewport("imageIdx-1", "-1")
  checkImageInViewport("imageIdx0", "0")
  checkImageInViewport("imageIdxEmotionalLeft", "EmotionalLeft")
  checkImageInViewport("imageIdxEmotionalRight", "EmotionalRight")
  checkImageInViewport("imageIdx1", "1")
  checkImageInViewport("imageIdx2", "2")
}

const checkImageInViewport = (nodeId: string, imagePoxIdx: string) => {
  const el = scrollContainer.value?.querySelector("#" + nodeId) as HTMLElement;
  if (!el) return;

  const rect = el.getBoundingClientRect();
  const parent = scrollContainer.value; // The scroll container
  if (!parent) return;

  const parentRect = parent.getBoundingClientRect();

  // Check if the center of the element is within the viewport of the scroll container
  const elementCenter = rect.left + rect.width / 2;
  const parentCenter = parentRect.left + parentRect.width / 2;

  // If the element is mostly visible in the center (allowing some margin)
  if (Math.abs(elementCenter - parentCenter) < rect.width / 2) {
    emits('switchImage', imagePoxIdx)
  }
}

/**
 grid layout variants
 */

const hasMainPosition = computed(() => !!props.image?.positions?.[0]?.filename)
const hasAdditionalItem = computed(() => !!props.image?.additionalItem?.filename)
const hasAdditionalPosition = computed(() => !!props.image?.additionalItem?.positions?.[0]?.filename)

const layoutVariant = computed(() => {
  if (!hasAdditionalItem.value) {
    return hasMainPosition.value ? 6 : 5
  }
  if (hasMainPosition.value && hasAdditionalPosition.value) {
    return 1
  }
  if (!hasMainPosition.value && hasAdditionalPosition.value) {
    return 2
  }
  if (hasMainPosition.value && !hasAdditionalPosition.value) {
    return 3
  }
  return 4
})

const brandLogoId = computed(() => {
  return (props.itemNumber || "").toString().substring(0, 2) === "R-" ? "R" : "S"
})

/** Controls whether the emotional set image (row 2) plays its animation or shows a static image. Defaults to false. */
const emotionalImageAnimated = computed(() => props.emotionalImageAnimated === true)

/**
 emotional image
 */

const emotionalImageUrl = computed(() => {
  const rightImageUrl = props.image?.additionalItem?.filename || "-"
  if (!props.image?.filename || !rightImageUrl) return ""
  const hash_left = props.image.filename.split("/")[props.image.filename.split("/").length - 1]
  const hash_right = rightImageUrl.split("/")[rightImageUrl.split("/").length - 1]
  return "https://conf.images.rubin.jewelry/item/image/set/emo-v1/" + hash_left + "/" + hash_right
})

</script>
<style scoped>
.no-scrollbar::-webkit-scrollbar {
  display: none;
}

.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
